import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Package,
  Plus,
  Search,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  Upload,
} from 'lucide-react';
import { inventoryAPI } from '../../services/inventoryAPI';
import { InventoryItem, InventoryCategory, InventoryStats as IInventoryStats } from '../../types/inventory';
import InventoryItemModal from './InventoryItemModal';
import CategoryModal from './CategoryModal';
import StockAdjustmentModal from './StockAdjustmentModal';
import InventoryList from './InventoryList';
import LowStockAlerts from './LowStockAlerts';
import InventoryStatsComponent from './InventoryStats';

const InventoryManagement: React.FC = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [stats, setStats] = useState<IInventoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'quantity' | 'value'>('name');
  const [activeTab, setActiveTab] = useState<'inventory' | 'low-stock' | 'transactions'>('inventory');
  
  // Modals
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  useEffect(() => {
    fetchInventoryData();
  }, []);

  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const [itemsRes, categoriesRes, statsRes] = await Promise.all([
        inventoryAPI.getItems(),
        inventoryAPI.getCategories(),
        inventoryAPI.getStats(),
      ]);
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = () => {
    setSelectedItem(null);
    setItemModalOpen(true);
  };

  const handleEditItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setItemModalOpen(true);
  };

  const handleAdjustStock = (item: InventoryItem) => {
    setSelectedItem(item);
    setStockModalOpen(true);
  };

  const handleDeleteItem = async (id: number) => {
    if (window.confirm(t('inventory.confirmDelete'))) {
      try {
        await inventoryAPI.deleteItem(id);
        fetchInventoryData();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleExportInventory = async () => {
    try {
      const response = await inventoryAPI.exportInventory();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting inventory:', error);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category_id?.toString() === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'quantity':
        return b.quantity_in_stock - a.quantity_in_stock;
      case 'value':
        return (b.quantity_in_stock * b.selling_price) - (a.quantity_in_stock * a.selling_price);
      default:
        return 0;
    }
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('inventory.title')}</h1>
        <p className="text-gray-600">{t('inventory.subtitle')}</p>
      </div>

      {/* Stats Overview */}
      {stats && <InventoryStatsComponent stats={stats} />}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'inventory'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('inventory')}
        >
          <Package className="w-5 h-5 inline-block mr-2" />
          {t('inventory.tabs.inventory')}
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'low-stock'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('low-stock')}
        >
          <AlertTriangle className="w-5 h-5 inline-block mr-2" />
          {t('inventory.tabs.lowStock')}
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'transactions'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('transactions')}
        >
          <TrendingUp className="w-5 h-5 inline-block mr-2" />
          {t('inventory.tabs.transactions')}
        </button>
      </div>

      {/* Main Content */}
      {activeTab === 'inventory' && (
        <>
          {/* Actions Bar */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4 w-full lg:w-auto">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('inventory.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('inventory.allCategories')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="name">{t('inventory.sortByName')}</option>
                <option value="quantity">{t('inventory.sortByQuantity')}</option>
                <option value="value">{t('inventory.sortByValue')}</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setCategoryModalOpen(true)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Filter className="w-5 h-5 inline-block mr-2" />
                {t('inventory.manageCategories')}
              </button>
              <button
                onClick={handleExportInventory}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Download className="w-5 h-5 inline-block mr-2" />
                {t('inventory.export')}
              </button>
              <button
                onClick={handleCreateItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5 inline-block mr-2" />
                {t('inventory.addItem')}
              </button>
            </div>
          </div>

          {/* Inventory List */}
          <InventoryList
            items={sortedItems}
            categories={categories}
            loading={loading}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onAdjustStock={handleAdjustStock}
          />
        </>
      )}

      {activeTab === 'low-stock' && (
        <LowStockAlerts onRefresh={fetchInventoryData} />
      )}

      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">
            {t('inventory.transactionsComingSoon')}
          </p>
        </div>
      )}

      {/* Modals */}
      {itemModalOpen && (
        <InventoryItemModal
          isOpen={itemModalOpen}
          onClose={() => setItemModalOpen(false)}
          onSuccess={fetchInventoryData}
          item={selectedItem}
          categories={categories}
        />
      )}

      {categoryModalOpen && (
        <CategoryModal
          isOpen={categoryModalOpen}
          onClose={() => setCategoryModalOpen(false)}
          onSuccess={fetchInventoryData}
        />
      )}

      {stockModalOpen && selectedItem && (
        <StockAdjustmentModal
          isOpen={stockModalOpen}
          onClose={() => setStockModalOpen(false)}
          onSuccess={fetchInventoryData}
          item={selectedItem}
        />
      )}
    </div>
  );
};

export default InventoryManagement;